import React, { useState, useRef, useEffect } from "react";
import "./ProjectPicklist.css";

function ProjectPicklist({ projects, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Transform backend shape → frontend shape
  const transformed = projects.map((p) => {
    const label = p.label || "";

    // Extract "Name (Client)"
    const match = label.match(/^(.*)\((.*)\)$/);

    if (match) {
      return {
        id: p.id,
        name: match[1].trim(),
        client: match[2].trim(),
      };
    }

    // Fallback if no parentheses
    return {
      id: p.id,
      name: label.trim(),
      client: "",
    };
  });

  // ⭐ Alphabetical sort by project name (case‑insensitive)
  const sorted = [...transformed].sort((a, b) =>
    a.name.localeCompare(b.name, "en", { sensitivity: "base" })
  );

  const selected = sorted.find((p) => String(p.id) === String(value));

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filtered = normalizedSearch
    ? sorted.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const client = (p.client || "").toLowerCase();
        const full = `${name} ${client}`.trim();
        return (
          name.startsWith(normalizedSearch) ||
          client.startsWith(normalizedSearch) ||
          full.includes(normalizedSearch)
        );
      })
    : sorted;

  const handleSelect = (project) => {
    onChange(project);
    setOpen(false);
    setSearchTerm("");
    setActiveIndex(0);
  };

  const getDisplayLabel = (project) =>
    project.client ? `${project.name} (${project.client})` : project.name;

  const renderHighlighted = (text) => {
    if (!normalizedSearch) return text;

    const source = text || "";
    const sourceLower = source.toLowerCase();
    const matchAt = sourceLower.indexOf(normalizedSearch);

    if (matchAt < 0) return source;

    const before = source.slice(0, matchAt);
    const match = source.slice(matchAt, matchAt + normalizedSearch.length);
    const after = source.slice(matchAt + normalizedSearch.length);

    return (
      <>
        {before}
        <mark className="picklist-highlight">{match}</mark>
        {after}
      </>
    );
  };

  const handleSearchKeyDown = (e) => {
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!filtered.length) return;
      setActiveIndex((prev) => (prev + 1) % filtered.length);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!filtered.length) return;
      setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (!filtered.length) return;
      handleSelect(filtered[activeIndex] || filtered[0]);
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setSearchTerm("");
      setActiveIndex(0);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearchTerm("");
        setActiveIndex(0);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchTerm, open]);

  return (
    <div className="picklist-container" ref={containerRef}>
      <div
        className="picklist-selected"
        onClick={() => {
          setOpen((o) => !o);
          setSearchTerm("");
          setActiveIndex(0);
        }}
      >
        {selected
          ? selected.client
            ? `${selected.name} (${selected.client})`
            : selected.name
          : "Select project"}
      </div>

      {open && (
        <div className="picklist-dropdown">
          <div className="picklist-search-wrap">
            <span className="picklist-search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                <path d="M10 3a7 7 0 1 0 4.3 12.5l5.1 5.1 1.4-1.4-5.1-5.1A7 7 0 0 0 10 3zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10z" />
              </svg>
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className="picklist-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Type to filter projects"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="picklist-item" aria-disabled="true">
              {sorted.length === 0 ? "No projects available" : "No matching projects"}
            </div>
          ) : (
            filtered.map((p, index) => (
              <div
                key={p.id}
                className={`picklist-item${index === activeIndex ? " is-active" : ""}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => handleSelect(p)}
              >
                {renderHighlighted(getDisplayLabel(p))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectPicklist;
